import { memo, useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { MaterialIcon, sanitizeHtml } from '@lapo/ui';
import {
  actionDecl,
  compareSignals,
  learningKeyOf,
  routingKeyForKindSender,
  type CreateActionKind,
  type LinkedArtifact,
  type ResolutionReason,
  type Signal,
  type SignalAction,
} from '@lapo/core';
import { SIGNAL_WAKE_EVENT, type SignalWakeDetail } from './signalsWake.js';

// HELM P4-5: the graduation accept scope (mirrors apiClient's `GrantScopeChoice`). `ask` = no grant,
// so it's excluded. The feed card defaults to `once` — the most conservative — so an accidental tap
// grants the mildest possible authorization; broaden it from the Autonomy panel.
export type GraduationScope = 'once' | 'subject' | 'always';
const GRADUATION_SCOPES: Array<{ value: GraduationScope; label: string }> = [
  { value: 'once', label: 'Just this once' },
  { value: 'subject', label: 'This subject' },
  { value: 'always', label: 'Always' },
];

/**
 * RECON R7 — the brief "closed" toast copy for an auto-resolved card, keyed by why it closed. A resolved
 * signal rides the feed transiently (server window), fires this once, then drops from the cards.
 */
export function resolvedToastCopy(reason: ResolutionReason | undefined): string {
  switch (reason) {
    case 'replied':
      return 'Closed — you replied';
    case 'reacted':
      return 'Closed — you reacted';
    case 'task-completed':
      return 'Completed in Todoist';
    case 'event-passed':
      return 'Event passed';
    case 'event-cancelled':
      return 'Event cancelled';
    case 'rsvp':
      return 'Closed — you RSVP’d';
    case 'email-archived':
      return 'Closed — you archived the email';
    default:
      return 'Closed';
  }
}

/**
 * DaySignals — the machine-owned feed below each day's journal (spec D1), rebuilt for AXIS (X13/X-04)
 * and refined for HONE: the card face shows the ONE primary verb + `Edit` (when a draft rides along);
 * the single ⋯ overflow is now a pure ACTION palette — a "Create" group (Draft / Task / Document /
 * Journal / … — spawn any artifact from the signal, P1) + a "Remove" group (undo an artifact created
 * from it) + "Later" (snooze) + Dismiss. The corner ✕ is the NEUTRAL "set aside" (trains nothing); the
 * relevance thumbs teach usefulness, and a 👎 reveals the rule-tuning follow-up (never show / show
 * quietly / always handle / spam / automated) that used to live in ⋯.
 * Graduation-proposal cards (X18) render the evidence line with an explicit
 * [Yes, auto from now on] / [Keep asking] pair. Nothing writes to Gmail/Calendar until the owner taps
 * the verb (draft-first — spec A1); auto-execution is server-side and never renders a card.
 *
 * See `docs/plans/action-system.md` §6 + `docs/plans/email-triage-actions.md` (A1–A22).
 */

/** The source email behind a draft-reply card, shown in the Review-draft dialog's scrollable pane. */
export interface SignalSourceEmail {
  from: string;
  fromAddress: string;
  subject: string;
  date: string;
  text: string;
  snippet: string;
}

/** The slice of the api client this component needs (kept narrow for testability). */
export interface SignalsApi {
  getSignals(dateKey: string, today?: string): Promise<Signal[]>;
  /** SETTLE-03 — on-read liveness pass; clears cards whose source was already handled. Optional (best-effort). */
  reconcileSignals?(): Promise<{ checked: number; cleared: number }>;
  markSignalSeen(signalId: string): Promise<Signal | null>;
  dismissSignal(signalId: string): Promise<Signal | null>;
  /** HONE — the neutral "✕ / I'm not sure": hides the card without teaching the learning loop. */
  setAsideSignal(signalId: string): Promise<Signal | null>;
  /** HONE — spawn an artifact (Draft/Task/Document/Journal/…) from a signal via the ⋯ Create menu. */
  createSignalArtifact(
    signalId: string,
    kind: CreateActionKind,
    opts?: { number?: string },
  ): Promise<{ signal: Signal; artifact?: LinkedArtifact; relinkRequired?: string } | null>;
  /** HONE — resolve candidate numbers for a ⋯ Create → Call (contacts → Google Maps → web). */
  resolveCallNumber(
    signalId: string,
  ): Promise<{ targetName: string; candidates: Array<{ number: string; source: 'contacts' | 'maps' | 'web'; label: string }> }>;
  /** HONE — remove an artifact created from a signal (⋯ Remove): runs the inverse op, drops the link. */
  removeSignalArtifact(signalId: string, artifactId: string): Promise<Signal | null>;
  snoozeSignal(signalId: string, days: number): Promise<Signal | null>;
  actSignal(
    signalId: string,
    opts?: { editedBody?: string; actionIndex?: number },
  ): Promise<{ signal: Signal; created?: { kind: string; ref: string }; relinkRequired?: string } | null>;
  rejectSignal(signalId: string): Promise<Signal | null>;
  markSpamSignal(
    signalId: string,
  ): Promise<{ signal: Signal; created?: { kind: string; ref: string }; relinkRequired?: string } | null>;
  markAutomatedSignal(signalId: string): Promise<Signal | null>;
  draftReply(signalId: string, opts: { accountId: string; messageId: string; sender?: string }): Promise<void>;
  /** Review-draft dialog — the source email behind a draft-reply card (scrollable pane); null when gone. */
  getSignalSourceEmail(signalId: string): Promise<SignalSourceEmail | null>;
  /** Review-draft dialog — regenerate the draft from the owner's teach comment; returns the new body. */
  regenerateDraft(signalId: string, opts: { guidance: string }): Promise<{ draftBody: string } | null>;
  /** Review-draft dialog — SEND the reply (client fires this only after its undo window); resolves the card.
   *  `archive: true` is "Send & archive" — archives the source email too; `archiveRelink` reports a missing scope. */
  sendSignalReply(
    signalId: string,
    opts: { body: string; archive?: boolean },
  ): Promise<{ ok?: boolean; relinkRequired?: string; archiveRelink?: string } | null>;
  resetTriage(): Promise<number>;
  // AXIS (X-05/X14): the ⋯ standing-rule shortcuts write trust rules; routing entries write routing rules.
  setTrustRule(ruleKey: string, policy: 'confirm' | 'gated' | 'auto' | 'escalate'): Promise<unknown>;
  removeTrustRule(ruleKey: string): Promise<void>;
  setRoutingRule(rule: { ruleKey: string; loudness?: 'loud' | 'quiet' | 'suppress'; priorityOverride?: 'info' | 'action' | 'urgent' }): Promise<unknown>;
  removeRoutingRule(ruleKey: string): Promise<void>;
  // AXIS (X18): graduation proposals. HELM P4-5 — accept carries the once|subject|always scope the
  // P4-2 route writes as a grant (omitted ⇒ server default `always`; the feed card defaults to the
  // conservative `once` so a mis-tap can't over-grant). Mirrors apiClient.acceptGraduation.
  acceptGraduation(ruleKey: string, scope?: GraduationScope): Promise<void>;
  declineGraduation(ruleKey: string): Promise<void>;
  // ECHO (D7/IMPL-E4): per-item "Not relevant" on the resurface digest — suppresses the one item
  // (180d) WITHOUT touching the card lifecycle (its own route, not an act).
  resurfaceNotRelevant(targetId: string): Promise<void>;
  // SIEVE (S7/S8): one-tap restore from the collapsed "filtered N" card.
  restoreParkedIngest(parkedId: string): Promise<boolean>;
  // TUNE (U1): the uniform capture — relevance / post-action quality / teach-text, on EVERY kind.
  recordSignalFeedback(
    signalId: string,
    input: { relevance?: 'useful' | 'not-useful'; actionQuality?: 'good' | 'not-good'; note?: string },
  ): Promise<boolean>;
  // HELM P1-8 (C9): the morning brief for a day — rendered at the top of the feed with reaction thumbs.
  // Optional (feature-gated / absent in tests) ⇒ the brief block simply doesn't render.
  getBrief?(dateKey: string): Promise<MorningBrief | null>;
  /**
   * HELM P2-1 — land a brief-thumb reaction (useful/noise/wrong). A `wrong` tap carries the four-way
   * `wrongCause` the popover captures (DL-21); P2-3 routes each cause.
   */
  recordBriefReaction?(
    deliveryId: string,
    reaction: 'useful' | 'noise' | 'wrong',
    wrongCause?: 'wrong-fact' | 'already-handled' | 'duplicate' | 'bad-timing',
  ): Promise<boolean>;
}

/** HELM P1-8 — one rendered brief line the web draws a reaction row against (C9). */
export interface MorningBriefItem {
  subjectKey: string;
  section: 'decision' | 'handled' | 'left-alone';
  title: string;
  detail: string;
}

/** HELM P1-8 — the morning brief for a day (the `GET /me/attention/brief/:dateKey` payload). */
export interface MorningBrief {
  dateKey: string;
  items: MorningBriefItem[];
  /** The ledger deliveryId the reaction thumbs patch; absent until the brief has been sent. */
  deliveryId?: string;
}

export interface DaySignalsProps {
  dateKey: string;
  today: string;
  api: SignalsApi | null;
  onOpenNote?: (noteId: string) => void;
  onOpenDate?: (dateKey: string) => void;
  /** `targetId` deep-links within the panel (WEAVE: the decision a `decision-updated` card points at). */
  onOpenPanel?: (panel: string, targetId?: string) => void;
}

const KIND_ICON: Record<string, string> = {
  'tracking-found': 'inventory_2',
  'pending-delivery': 'local_shipping',
  'task-nudge': 'bolt',
  'bill-due': 'receipt_long',
  'task-added': 'add_task',
  'note-added': 'edit_note',
  'action-proposed': 'draft',
  'action-escalated': 'priority_high',
  'triage-audit': 'inbox',
  'financial-alert': 'account_balance',
  'slack-needs-reply': 'forum',
  'graduation-proposal': 'auto_awesome',
  'autonomy-recap': 'fact_check',
  'decisions-logged': 'gavel',
  'decision-revisit-due': 'event_repeat',
  'decision-updated': 'timeline',
  // ECHO — the weekly forgotten-connections digest (note-resurfacing.md D4).
  'resurface-digest': 'history',
  // SIEVE — ingest triage: the collapsed "filtered N" card + the "worth a think" flag (ingest-triage.md S8/S9).
  'ingest-filtered': 'filter_alt',
  'ingest-think': 'psychology',
  // TUNE — the weekly "What Lapo learned" guidance digest (signal-feedback.md U6).
  'guidance-digest': 'school',
  // CREEL — Amazon via Clavero shopping signals (amazon-clavero-integration.md §9).
  'order-placed': 'shopping_cart',
  'shopping-attention': 'shopping_bag',
  'amazon-match-digest': 'inventory',
};

const UNDO_MS = 5000;
const DWELL_MS = 700;

function primaryAction(signal: Signal): SignalAction | undefined {
  return signal.actions.find((a) => a.primary) ?? signal.actions[0];
}
/** A draft-carrying proposal offers an inline Edit on the face (spec A8/X13). */
function editableDraft(signal: Signal): SignalAction | undefined {
  return signal.actions.find(
    (a) => a.kind === 'create-draft-reply' || a.kind === 'propose-times' || a.kind === 'unsubscribe' || a.kind === 'slack-send',
  );
}
function senderOf(signal: Signal): string | undefined {
  return primaryAction(signal)?.payload?.sender;
}
/**
 * Whether to offer an on-demand "Draft reply" (in ⋯): an email card Lapo didn't already draft a reply
 * for, whose action payload carries the ids the draft-reply request needs (accountId + messageId).
 */
function canDraftReply(signal: Signal): boolean {
  if (triageSource(signal) !== 'email' || editableDraft(signal)) return false;
  const p = primaryAction(signal)?.payload;
  return Boolean(p?.accountId && p?.messageId);
}

/**
 * Whether tapping the primary verb should open the Review-draft dialog rather than acting immediately: an
 * email card whose primary action is a ready reply draft (`create-draft-reply` carrying a body). Calendar
 * `propose-times` cards keep the plain approve tap.
 */
function isReviewableDraft(signal: Signal): boolean {
  const p = primaryAction(signal);
  return p?.kind === 'create-draft-reply' && Boolean(p.payload?.draftBody);
}

/**
 * Whether a triage card is about an email or a calendar event, so the feed can show a distinct icon.
 * The action-planner encodes the kind in `source` (`engine:action-planner:calendar`), which works even
 * for a buttonless calendar escalation; older signals fall back to the action payload's id shape.
 */
function triageSource(signal: Signal): 'email' | 'calendar' | undefined {
  if (signal.source.endsWith(':calendar')) return 'calendar';
  if (signal.source.endsWith(':email')) return 'email';
  const p = primaryAction(signal)?.payload;
  if (p?.eventId) return 'calendar';
  if (p?.threadId || p?.messageId) return 'email';
  return undefined;
}

/** HONE — the display noun + glyph for each ⋯ "Create" artifact. */
const CREATE_META: Record<CreateActionKind, { label: string; icon: string }> = {
  draft: { label: 'Draft', icon: 'edit_note' },
  task: { label: 'Task', icon: 'check_circle' },
  appointment: { label: 'Appointment', icon: 'event' },
  document: { label: 'Document', icon: 'description' },
  journal: { label: 'Journal', icon: 'menu_book' },
  call: { label: 'Call', icon: 'call' },
};

/**
 * HONE — which ⋯ "Create" actions to offer for a signal. Task/Journal/Document are always available;
 * Draft only when the card carries an email thread to reply to. Appointment + Call arrive in P6 (their
 * server executors aren't wired yet), so they're withheld here rather than shown as failing taps.
 */
function createActionsFor(signal: Signal): CreateActionKind[] {
  const kinds: CreateActionKind[] = [];
  if (canDraftReply(signal)) kinds.push('draft');
  kinds.push('task', 'appointment', 'journal', 'document', 'call');
  return kinds;
}

/** The card's leading icon: email/calendar for triage cards, else the per-kind icon. */
function iconFor(signal: Signal): string {
  if (signal.kind === 'graduation-proposal') return KIND_ICON[signal.kind]!;
  const src = triageSource(signal);
  if (src === 'calendar') return 'event';
  if (src === 'email') return 'mail';
  return KIND_ICON[signal.kind] ?? 'notifications';
}

/**
 * A compact local-time label for when the subject occurs/occurred (calendar start / email received),
 * formatted in the viewer's own timezone. Undefined when the signal carries no natural time.
 */
function formatWhen(signal: Signal): string | undefined {
  if (signal.occurredAt == null) return undefined;
  const d = new Date(signal.occurredAt);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** The face verb for an action: its own label (already disposition-specific) else the registry verb (X13). */
function verbFor(action: SignalAction): string {
  return action.label || actionDecl(action.kind)?.verb || 'Do it';
}

/** The disposition-verb word used in the ⋯ standing-rule copy ("Always archive …"). */
function ruleVerbFor(signal: Signal): string {
  const primary = primaryAction(signal);
  if (!primary) return 'handle';
  const decl = actionDecl(primary.kind);
  if (!decl || decl.navigation) return 'handle';
  return decl.verb.toLowerCase();
}

/**
 * The trust-rule key the ⋯ shortcuts write for this card (X-05): sender-grain × the primary action's
 * disposition, from the shared registry. For an escalation/navigation card (no executable action) the
 * "surface/ask" shortcuts fall back to the sender-wide escalate marker key.
 */
function trustKeyOf(signal: Signal): string | null {
  const primary = primaryAction(signal);
  if (!primary) return null;
  const key = learningKeyOf(primary.kind, primary.payload);
  if (key) return key;
  const sender = senderOf(signal);
  return sender ? `sender:${sender.toLowerCase()}#escalate` : null;
}

// ARCH-006 — memoized: App() re-renders the whole shell on every poll tick (60s agenda, 5-min
// shipments, voice), but DaySignals' props are all value/ref-stable (string dateKey/today, the stable
// `api` client, useCallback handlers), so shallow compare skips its subtree on those unrelated ticks.
function DaySignalsInner({ dateKey, today, api, onOpenNote, onOpenDate, onOpenPanel }: DaySignalsProps): ReactElement | null {
  const [signals, setSignals] = useState<Signal[]>([]);
  const [pending, setPending] = useState<{
    signal: Signal;
    timer: ReturnType<typeof setTimeout>;
    // HONE — 'set-aside' commits the neutral ✕ (no learning); 'dismiss' is the trained negative.
    // 'send' is the Review-draft "Send email" held behind the undo window — commit fires the real send.
    mode: 'dismiss' | 'set-aside' | 'send';
    /** The reply body to send when `mode === 'send'` and the undo window elapses. */
    body?: string;
    /** "Send & archive": archive the source email alongside the send once the undo window elapses. */
    archive?: boolean;
  } | null>(null);
  const [notice, setNotice] = useState<{ text: string; undo?: () => void; qualityFor?: string } | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [editing, setEditing] = useState<{ signalId: string; body: string } | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  // HELM P4-5 — per-proposal graduation scope (once|subject|always), default `once` (conservative).
  const [graduationScope, setGraduationScope] = useState<Record<string, GraduationScope>>({});
  /** ECHO — resurface items the owner marked "Not relevant" this session (hidden locally at once). */
  const [droppedResurface, setDroppedResurface] = useState<ReadonlySet<string>>(new Set());
  /** SIEVE — parked items restored this session (hidden locally at once). */
  const [restoredParked, setRestoredParked] = useState<ReadonlySet<string>>(new Set());
  /** TUNE U1 — signals already relevance-voted this session (buttons collapse to a thanks mark). */
  const [voted, setVoted] = useState<ReadonlyMap<string, 'useful' | 'not-useful'>>(new Map());
  /** HELM P1-8 — today's morning brief (C9), rendered at the top of the feed with reaction thumbs. */
  const [brief, setBrief] = useState<MorningBrief | null>(null);
  /** HELM P1-8 — brief rows already reacted to this session (collapse the row's thumbs to a thanks mark). */
  const [briefVoted, setBriefVoted] = useState<ReadonlyMap<string, 'useful' | 'noise' | 'wrong'>>(new Map());
  /** HELM P2-1 (DL-21) — the brief row whose `wrong` four-way cause popover is open (null = none). */
  const [briefWrongFor, setBriefWrongFor] = useState<string | null>(null);
  /** HONE — which signal's 👎 relevance follow-up (relocated trust/routing rules) is open. */
  const [followUpFor, setFollowUpFor] = useState<string | null>(null);
  /** HONE — the pending ⋯ Create → Call confirmation (owner confirms/edits the number before dialing). */
  const [callFor, setCallFor] = useState<{ signal: Signal; targetName: string; number: string; source?: string; resolving: boolean } | null>(null);
  /** TUNE U1c — the open "Teach Lapo…" box. */
  const [teach, setTeach] = useState<{ signalId: string; text: string } | null>(null);
  /**
   * The open Review-draft dialog (email reply cards): the source email + the editable draft, plus the
   * inline Teach-to-iterate box. `source: null` while loading or when the message is unavailable.
   */
  const [review, setReview] = useState<{
    signal: Signal;
    draft: string;
    source: SignalSourceEmail | null;
    loadingSource: boolean;
    teachOpen: boolean;
    teachText: string;
    regenerating: boolean;
  } | null>(null);
  const dwellTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  /** RECON R7 — resolved signalIds already toasted, so a reload within the server window doesn't re-toast. */
  const toastedResolved = useRef(new Set<string>());
  /**
   * Cards the user just cleared locally (dismiss / set-aside / snooze / clear-all / "never show"),
   * awaiting server-side removal. The server commit is deferred through the undo window while a
   * refetch (focus / visibility / push wake) can fire, so without this a stale feed would resurrect
   * a card the user just cleared — it flashes back and takes a second click to clear (the reported
   * double-dismiss race). Ids are dropped once the feed stops returning them (server confirmed) or on undo.
   */
  const suppressedIds = useRef(new Set<string>());

  const flashNotice = useCallback((text: string, undo?: () => void, qualityFor?: string) => {
    setNotice({ text, undo, qualityFor });
    // The post-action quality ask (U1b) lingers longer than a plain toast — it's a question.
    setTimeout(() => setNotice(null), qualityFor ? UNDO_MS * 3 : UNDO_MS);
  }, []);

  const load = useCallback(async () => {
    if (!api) return;
    try {
      const rows = await api.getSignals(dateKey, today);
      // RECON R7/R15 — a card that auto-resolved rides the feed transiently: show its "closed" toast once,
      // then keep it OUT of the rendered cards (it's terminal). Reconciliation-driven, so distinct from a
      // local dismiss/act (which the user just did and needs no toast).
      for (const s of rows) {
        if (s.status === 'resolved' && !toastedResolved.current.has(s.signalId)) {
          toastedResolved.current.add(s.signalId);
          flashNotice(resolvedToastCopy(s.resolutionReason));
        }
      }
      // Keep locally-cleared cards hidden until the server drops them: the dismiss commit is deferred
      // through the undo window, so a refetch that lands first still sees them live. Once the feed no
      // longer returns an id, the server has caught up — stop suppressing it (and prune the set).
      const present = new Set(rows.map((r) => r.signalId));
      for (const id of suppressedIds.current) {
        if (!present.has(id)) suppressedIds.current.delete(id);
      }
      setSignals(rows.filter((r) => !suppressedIds.current.has(r.signalId)).sort(compareSignals));
    } catch {
      setSignals([]);
    }
  }, [api, dateKey, today, flashNotice]);

  useEffect(() => {
    void load();
  }, [load]);

  // HELM P1-8 (C9) — load today's morning brief. Offline-and-hydrate (CLAUDE.md): paint from the
  // per-day localStorage cache INSTANTLY, then reconcile from the server; a fetch error leaves the cached
  // brief untouched (never blanks a good paint). The brief is a read item — no mutation rides this effect.
  useEffect(() => {
    if (!api?.getBrief) {
      setBrief(null);
      return;
    }
    const cacheKey = `lapo:brief:${dateKey}`;
    try {
      const cached = typeof localStorage !== 'undefined' ? localStorage.getItem(cacheKey) : null;
      if (cached) setBrief(JSON.parse(cached) as MorningBrief);
    } catch {
      /* a corrupt cache entry is ignored — the server reconcile below fixes it */
    }
    let cancelled = false;
    void api
      .getBrief(dateKey)
      .then((b) => {
        if (cancelled) return;
        setBrief(b);
        try {
          if (typeof localStorage !== 'undefined') {
            if (b) localStorage.setItem(cacheKey, JSON.stringify(b));
            else localStorage.removeItem(cacheKey);
          }
        } catch {
          /* storage full / unavailable — the in-memory brief still renders */
        }
      })
      .catch(() => {
        /* offline / error — keep the cached paint (SWR error-suppress) */
      });
    return () => {
      cancelled = true;
    };
  }, [api, dateKey]);

  // SETTLE-03 (DL-5) — after the feed paints, run the on-read liveness pass on today's instance so a card
  // whose source was already handled elsewhere (archived in Gmail, package delivered, decision closed) is
  // dropped a beat later. Server-side cooldown (~10 min) keeps this cheap across focus refetches; reload
  // only when it actually cleared something. Never blocks first paint (this is a separate, deferred effect).
  useEffect(() => {
    if (!api?.reconcileSignals || dateKey !== today) return;
    let cancelled = false;
    void api
      .reconcileSignals()
      .then((res) => {
        if (!cancelled && res && res.cleared > 0) void load();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, dateKey, today, load]);

  // Dismiss the ⋯ overflow menu on an outside click or Escape (it otherwise only closed by re-clicking
  // the ⋯ button or picking an item). The `.lapo-signal-tune` wrapper holds both the button and the menu,
  // so a click landing inside it is left alone; anything else closes the open menu.
  useEffect(() => {
    if (menuFor === null) return;
    const onDown = (e: MouseEvent): void => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target || !target.closest('.lapo-signal-tune')) setMenuFor(null);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenuFor(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuFor]);

  useEffect(() => {
    if (!api) return;
    const onWake = () => {
      if (document.visibilityState !== 'hidden') void load();
    };
    const onPushWake = (e: Event) => {
      const detail = (e as CustomEvent<SignalWakeDetail>).detail;
      if (!detail?.dateKey || detail.dateKey === dateKey) void load();
    };
    // Only TODAY's instance re-fetches on refocus (audit F6): the journal keeps many day instances
    // mounted, and having every one of them refire GET /signals on each focus/visibility flip was a
    // request storm. Past/future days still hear targeted push wakes (filtered by dateKey below).
    const isCurrentDay = dateKey === today;
    if (isCurrentDay) {
      window.addEventListener('focus', onWake);
      document.addEventListener('visibilitychange', onWake);
    }
    window.addEventListener(SIGNAL_WAKE_EVENT, onPushWake);
    return () => {
      if (isCurrentDay) {
        window.removeEventListener('focus', onWake);
        document.removeEventListener('visibilitychange', onWake);
      }
      window.removeEventListener(SIGNAL_WAKE_EVENT, onPushWake);
    };
  }, [api, load, dateKey, today]);

  // --- Seen-on-view (spec D14) --------------------------------------------------------------------
  const markSeen = useCallback(
    (id: string) => {
      if (!api) return;
      setSignals((prev) => prev.map((s) => (s.signalId === id && !s.seenAt ? { ...s, seenAt: Date.now() } : s)));
      void api.markSignalSeen(id);
    },
    [api],
  );
  // ONE IntersectionObserver per DaySignals instance (audit F6 — `observe` used to construct a fresh
  // observer per card per re-render and only self-disconnected after the dwell, leaking observers on
  // every render). Cards register through the map (element → signalId); the shared handler runs the
  // dwell timers and un-observes once seen. `markSeenRef` keeps the observer callback current without
  // recreating the observer.
  const markSeenRef = useRef(markSeen);
  markSeenRef.current = markSeen;
  const observedEls = useRef(new Map<Element, string>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const getObserver = useCallback((): IntersectionObserver | null => {
    if (observerRef.current) return observerRef.current;
    if (typeof IntersectionObserver === 'undefined') return null;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const signalId = observedEls.current.get(e.target);
        if (!signalId) continue;
        if (e.isIntersecting) {
          if (!dwellTimers.current.has(signalId)) {
            dwellTimers.current.set(
              signalId,
              setTimeout(() => {
                dwellTimers.current.delete(signalId);
                markSeenRef.current(signalId);
                observedEls.current.delete(e.target);
                observerRef.current?.unobserve(e.target);
              }, DWELL_MS),
            );
          }
        } else {
          const t = dwellTimers.current.get(signalId);
          if (t) {
            clearTimeout(t);
            dwellTimers.current.delete(signalId);
          }
        }
      }
    });
    observerRef.current = io;
    return io;
  }, []);
  const observe = useCallback(
    (el: HTMLElement | null, signal: Signal) => {
      if (!el || signal.seenAt) return;
      const io = getObserver();
      if (!io) return;
      // Idempotent: React re-invokes inline ref callbacks each render with the same element.
      if (observedEls.current.get(el) === signal.signalId) return;
      observedEls.current.set(el, signal.signalId);
      io.observe(el);
    },
    [getObserver],
  );
  // Tear down the shared observer and every pending dwell timer on unmount (the timers used to leak).
  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      for (const t of dwellTimers.current.values()) clearTimeout(t);
      dwellTimers.current.clear();
      observedEls.current.clear();
    },
    [],
  );

  const removeLocally = useCallback((id: string) => {
    suppressedIds.current.add(id);
    setSignals((prev) => prev.filter((s) => s.signalId !== id));
  }, []);
  const replaceLocally = useCallback(
    (updated: Signal) => setSignals((prev) => prev.map((x) => (x.signalId === updated.signalId ? updated : x))),
    [],
  );

  // --- Dismiss with undo (spec D15) ---------------------------------------------------------------
  const commitPending = useCallback(() => {
    setPending((p) => {
      if (p && api) {
        if (p.mode === 'send') {
          // The undo window elapsed — actually send. A relink gap restores the card + prompts a reconnect.
          void api.sendSignalReply(p.signal.signalId, { body: p.body ?? '', archive: p.archive }).then((res) => {
            if (res?.relinkRequired) {
              // The send didn't land — the card comes back, so stop suppressing it (a refetch would
              // otherwise re-hide the just-restored card).
              suppressedIds.current.delete(p.signal.signalId);
              setSignals((prev) => [...prev, p.signal].sort(compareSignals));
              flashNotice('Reconnect Google to let Lapo send this reply.');
            } else if (res?.archiveRelink) {
              // The reply sent, but archiving needs the broader Gmail scope — the mail stays in the inbox.
              flashNotice('Reply sent. Reconnect Google to let Lapo archive it.');
            }
          });
        } else {
          void (p.mode === 'set-aside' ? api.setAsideSignal(p.signal.signalId) : api.dismissSignal(p.signal.signalId));
        }
      }
      return null;
    });
  }, [api, flashNotice]);
  const removeWithUndo = useCallback(
    (signal: Signal, mode: 'dismiss' | 'set-aside') => {
      commitPending();
      setMenuFor(null);
      removeLocally(signal.signalId);
      const timer = setTimeout(commitPending, UNDO_MS);
      setPending({ signal, timer, mode });
    },
    [commitPending, removeLocally],
  );
  const dismiss = useCallback((signal: Signal) => removeWithUndo(signal, 'dismiss'), [removeWithUndo]);
  /** HONE — the neutral "✕ / I'm not sure": hides the card without teaching the learning loop. */
  const setAside = useCallback((signal: Signal) => removeWithUndo(signal, 'set-aside'), [removeWithUndo]);
  const undo = useCallback(() => {
    setPending((p) => {
      if (!p) return null;
      clearTimeout(p.timer);
      // The card is coming back — stop suppressing it so a refetch keeps it, not just this restore.
      suppressedIds.current.delete(p.signal.signalId);
      setSignals((prev) => [...prev, p.signal].sort(compareSignals));
      return null;
    });
  }, []);
  const snooze = useCallback(
    (signal: Signal, days: number) => {
      setMenuFor(null);
      removeLocally(signal.signalId);
      if (api) void api.snoozeSignal(signal.signalId, days);
    },
    [api, removeLocally],
  );

  // --- HONE: the ⋯ "Create"/"Remove" artifact actions (P1 endpoints) ------------------------------
  const createArtifact = useCallback(
    async (signal: Signal, kind: CreateActionKind) => {
      setMenuFor(null);
      if (!api) return;
      const res = await api.createSignalArtifact(signal.signalId, kind).catch(() => null);
      if (res?.relinkRequired) {
        flashNotice('Reconnect Google to let Lapo do that.');
        return;
      }
      if (!res?.signal) {
        flashNotice(`Couldn’t create that ${CREATE_META[kind].label.toLowerCase()}.`);
        return;
      }
      replaceLocally(res.signal);
      flashNotice(`${CREATE_META[kind].label} created.`);
    },
    [api, flashNotice, replaceLocally],
  );
  const removeArtifact = useCallback(
    async (signal: Signal, artifact: LinkedArtifact) => {
      setMenuFor(null);
      if (!api) return;
      const updated = await api.removeSignalArtifact(signal.signalId, artifact.artifactId).catch(() => null);
      if (updated) replaceLocally(updated);
      flashNotice(`${CREATE_META[artifact.kind].label} removed.`);
    },
    [api, flashNotice, replaceLocally],
  );

  // --- HONE: ⋯ Create → Call — resolve a number (contacts → Maps → web), then CONFIRM before dialing ---
  const startCall = useCallback(
    async (signal: Signal) => {
      setMenuFor(null);
      if (!api) return;
      // Open the confirm immediately (resolving), so a real outbound call is never a single tap.
      setCallFor({ signal, targetName: senderOf(signal) ?? signal.title, number: '', source: undefined, resolving: true });
      const res = await api.resolveCallNumber(signal.signalId).catch(() => null);
      const best = res?.candidates[0];
      setCallFor((prev) =>
        prev && prev.signal.signalId === signal.signalId
          ? { ...prev, targetName: res?.targetName ?? prev.targetName, number: best?.number ?? '', source: best?.source, resolving: false }
          : prev,
      );
    },
    [api],
  );
  const confirmCall = useCallback(async () => {
    const c = callFor;
    if (!c || !api || !c.number.trim()) return;
    setCallFor(null);
    const res = await api.createSignalArtifact(c.signal.signalId, 'call', { number: c.number.trim() }).catch(() => null);
    if (!res?.signal) {
      flashNotice('Couldn’t place the call — check voice setup and the number.');
      return;
    }
    replaceLocally(res.signal);
    flashNotice(`Calling ${c.targetName}…`);
  }, [callFor, api, flashNotice, replaceLocally]);

  // --- Approve / Edit / Reject a triage action (ACT-02/03, X13) -----------------------------------
  const act = useCallback(
    async (signal: Signal, opts?: { editedBody?: string; actionIndex?: number }) => {
      removeLocally(signal.signalId);
      setEditing(null);
      setMenuFor(null);
      if (!api) return;
      const res = await api.actSignal(signal.signalId, opts).catch(() => null);
      if (res?.relinkRequired) {
        // Not re-linked for the escalated scope — restore the card and tell the owner (spec A10).
        suppressedIds.current.delete(signal.signalId);
        setSignals((prev) => [...prev, signal].sort(compareSignals));
        flashNotice('Reconnect Google to grant Lapo permission for this action.');
        return;
      }
      const action = opts?.actionIndex != null ? signal.actions[opts.actionIndex] : primaryAction(signal);
      if (res?.created?.kind === 'note') onOpenNote?.(res.created.ref);
      else if (action?.kind === 'open-note' && action.payload?.noteId) onOpenNote?.(action.payload.noteId);
      else if ((action?.kind === 'open-note' || action?.kind === 'create-note') && action.payload?.dateKey)
        onOpenDate?.(action.payload.dateKey);
      else if (action?.kind === 'open-panel' && action.payload?.panel) onOpenPanel?.(action.payload.panel, action.payload.targetId);
      else if (res?.created?.kind === 'task') onOpenPanel?.('Today');
      else if (action?.kind === 'link' && action.payload?.url) window.open(action.payload.url, '_blank', 'noopener');
      else if (res?.created?.kind === 'draft') {
        // TUNE U1b — the draft toast doubles as the quality ask.
        flashNotice('Draft ready in Gmail.', undefined, signal.signalId);
        return;
      }
      // TUNE U1b — after an EXECUTED action (something was created/written), ask "good?" in the
      // notice bar. Pure-navigation taps (open/link) aren't actions worth rating.
      const executed = Boolean(res?.created) || (action ? !['open-note', 'open-panel', 'link', 'snooze', 'dismiss'].includes(action.kind) : false);
      if (executed) flashNotice(`Done: ${signal.title}`, undefined, signal.signalId);
    },
    [api, onOpenNote, onOpenDate, onOpenPanel, removeLocally, flashNotice],
  );

  // --- Mark as spam (SPAM-*): archive + mark-read in Gmail and teach the classifier ---------------
  const markSpam = useCallback(
    async (signal: Signal) => {
      removeLocally(signal.signalId);
      setMenuFor(null);
      if (!api) return;
      const res = await api.markSpamSignal(signal.signalId).catch(() => null);
      if (res?.relinkRequired) {
        // Needs the gmail.modify scope — restore the card and prompt a re-link (spec A10).
        suppressedIds.current.delete(signal.signalId);
        setSignals((prev) => [...prev, signal].sort(compareSignals));
        flashNotice('Reconnect Google to let Lapo archive spam.');
        return;
      }
      flashNotice('Marked spam — archived, and future mail from this sender will be auto-handled.');
    },
    [api, removeLocally, flashNotice],
  );

  // --- Mark as automated: teach the sender is a machine and handle its mail quietly from now on -------
  const markAutomated = useCallback(
    async (signal: Signal) => {
      removeLocally(signal.signalId);
      setMenuFor(null);
      if (!api) return;
      await api.markAutomatedSignal(signal.signalId).catch(() => null);
      flashNotice('Marked as automated — future mail from this sender will be handled quietly.');
    },
    [api, removeLocally, flashNotice],
  );

  // --- On-demand "Draft reply" (async): enqueue generation, clear the card, notice the owner ----------
  const requestDraft = useCallback(
    (signal: Signal) => {
      const p = primaryAction(signal)?.payload;
      if (!api || !p?.accountId || !p?.messageId) return;
      removeLocally(signal.signalId);
      setMenuFor(null);
      void api.draftReply(signal.signalId, { accountId: p.accountId, messageId: p.messageId, sender: p.sender });
      flashNotice('Drafting your reply — it’ll appear here to review in a moment.');
    },
    [api, removeLocally, flashNotice],
  );

  // --- Review-draft dialog: open with the draft, lazy-load the source email, mobile-back support -------
  const closeReview = useCallback((opts?: { fromPopState?: boolean }) => {
    setReview(null);
    // We pushed a history entry on open so Android's hardware Back closes the dialog. When we close for any
    // OTHER reason (✕ / Cancel / backdrop / Escape), consume that entry so we don't leave a dangling state.
    if (!opts?.fromPopState) {
      try {
        if ((window.history.state as { lapoReview?: string } | null)?.lapoReview) window.history.back();
      } catch {
        /* history unavailable — nothing to unwind */
      }
    }
  }, []);
  const openReview = useCallback(
    (signal: Signal) => {
      setMenuFor(null);
      const draft = editableDraft(signal);
      setReview({
        signal,
        draft: draft?.payload?.draftBody ?? '',
        source: null,
        loadingSource: true,
        teachOpen: false,
        teachText: '',
        regenerating: false,
      });
      try {
        window.history.pushState({ lapoReview: signal.signalId }, '');
      } catch {
        /* history unavailable — Back just won't close the dialog */
      }
      if (!api) {
        setReview((r) => (r ? { ...r, loadingSource: false } : r));
        return;
      }
      void api
        .getSignalSourceEmail(signal.signalId)
        .then((email) => setReview((r) => (r && r.signal.signalId === signal.signalId ? { ...r, source: email, loadingSource: false } : r)))
        .catch(() => setReview((r) => (r ? { ...r, loadingSource: false } : r)));
    },
    [api],
  );
  // "Add draft" — reuse the approve path (creates the Gmail draft from the edited body + hides the card).
  const addDraftFromReview = useCallback(() => {
    const r = review;
    if (!r) return;
    closeReview();
    void act(r.signal, { editedBody: r.draft });
  }, [review, closeReview, act]);
  // "Send email" / "Send & archive" — hide the card, then send behind the shared undo window (Undo
  // cancels the send). When `archive` is set the source email is archived alongside the send.
  const sendFromReview = useCallback(
    (archive: boolean) => {
      const r = review;
      if (!r || !r.draft.trim()) return;
      closeReview();
      commitPending();
      removeLocally(r.signal.signalId);
      const timer = setTimeout(commitPending, UNDO_MS);
      setPending({ signal: r.signal, timer, mode: 'send', body: r.draft, archive });
    },
    [review, closeReview, commitPending, removeLocally],
  );
  // "Teach" — regenerate the draft from the owner's note AND record it as feedback (owner's chosen mode).
  const teachInReview = useCallback(async () => {
    const r = review;
    if (!r || !api) return;
    const note = r.teachText.trim();
    if (!note) return;
    setReview((x) => (x ? { ...x, regenerating: true } : x));
    void api.recordSignalFeedback(r.signal.signalId, { note });
    const res = await api.regenerateDraft(r.signal.signalId, { guidance: note }).catch(() => null);
    setReview((x) =>
      x && x.signal.signalId === r.signal.signalId
        ? { ...x, regenerating: false, teachOpen: false, teachText: '', draft: res?.draftBody ?? x.draft }
        : x,
    );
  }, [review, api]);

  // Escape closes the dialog; Android's hardware Back pops the history entry we pushed on open.
  useEffect(() => {
    if (!review) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeReview();
    };
    const onPop = (): void => closeReview({ fromPopState: true });
    document.addEventListener('keydown', onKey);
    window.addEventListener('popstate', onPop);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
    };
  }, [review, closeReview]);

  // --- Clear the whole triage feed (backlog reset) -------------------------------------------------
  const clearAll = useCallback(async () => {
    if (!api) return;
    // "Clear all" clears EVERY visible card, not just triage: resetTriage() sweeps the triage kinds in
    // one shot, and every other still-open card is dismissed individually. Snapshot the non-triage cards
    // before optimistically emptying the feed.
    const TRIAGE = new Set(['action-proposed', 'action-escalated', 'triage-audit']);
    const toDismiss = signals.filter((s) => s.status !== 'resolved' && s.status !== 'dismissed' && !TRIAGE.has(s.kind));
    // Suppress every card we're clearing (triage cards reset server-side too) so a refetch that lands
    // before the server catches up can't flash the whole feed back and force a second Clear all.
    for (const s of signals) if (s.status !== 'resolved') suppressedIds.current.add(s.signalId);
    setSignals((prev) => prev.filter((s) => s.status === 'resolved'));
    const [triageCount] = await Promise.all([
      api.resetTriage().catch(() => 0),
      ...toDismiss.map((s) => api.dismissSignal(s.signalId).catch(() => null)),
    ]);
    const total = triageCount + toDismiss.length;
    flashNotice(`Cleared ${total} ${total === 1 ? 'card' : 'cards'}.`);
  }, [api, flashNotice, signals]);

  // --- ⋯ "This action" standing-rule shortcuts (X-05): write TrustRules with undo -------------------
  const tuneTrust = useCallback(
    (signal: Signal, policy: 'auto' | 'confirm' | 'escalate') => {
      const sender = senderOf(signal);
      const ruleKey = trustKeyOf(signal);
      setMenuFor(null);
      if (!api || !sender || !ruleKey) return;
      void api.setTrustRule(ruleKey, policy);
      const label =
        policy === 'auto'
          ? `Always ${ruleVerbFor(signal)} from ${sender} — Lapo will act on its own.`
          : policy === 'escalate'
            ? `Always surfacing ${sender}.`
            : `Always asking about ${sender}.`;
      flashNotice(label, () => void api.removeTrustRule(ruleKey));
    },
    [api, flashNotice],
  );

  // --- ⋯ "This signal" routing shortcuts (X26/X31): write RoutingRules with undo --------------------
  const tuneRouting = useCallback(
    (
      signal: Signal,
      patch: { loudness?: 'loud' | 'quiet' | 'suppress'; priorityOverride?: 'info' | 'action' | 'urgent' },
      opts?: { alsoAuto?: boolean; clearCard?: boolean },
    ) => {
      const sender = senderOf(signal);
      const routingKey = sender ? routingKeyForKindSender(signal.kind, sender) : `kind:${signal.kind}`;
      setMenuFor(null);
      if (!api) return;
      void api.setRoutingRule({ ruleKey: routingKey, ...patch });
      // X31 — the explicit combo: "Never show AND always {verb}" writes BOTH rules in one tap; the
      // autonomy half is still an explicit owner grant (suppress alone never implies auto).
      const trustKey = opts?.alsoAuto ? trustKeyOf(signal) : null;
      if (trustKey) void api.setTrustRule(trustKey, 'auto');
      // The routing rule only suppresses FUTURE emits; already-materialized cards must be dismissed
      // server-side or they reappear on the next load. "Never show these" clears every currently-visible
      // card sharing this routing key (same kind+sender), not just the tapped one.
      if (opts?.clearCard) {
        const candidateKey = (c: Signal) => {
          const cs = senderOf(c);
          return cs ? routingKeyForKindSender(c.kind, cs) : `kind:${c.kind}`;
        };
        for (const c of signals) {
          if (c.status === 'dismissed' || c.status === 'resolved') continue;
          if (c.signalId !== signal.signalId && candidateKey(c) !== routingKey) continue;
          removeLocally(c.signalId);
          void api.dismissSignal(c.signalId).catch(() => {});
        }
      }
      const what =
        patch.loudness === 'suppress'
          ? trustKey
            ? `Never showing these — and Lapo will ${ruleVerbFor(signal)} them on its own.`
            : 'Never showing signals like this. The action still won’t run on its own.'
          : patch.loudness === 'quiet'
            ? 'Showing these quietly (audit line).'
            : patch.priorityOverride
              ? `Always ${patch.priorityOverride} from now on.`
              : 'Updated.';
      flashNotice(what, () => {
        void api.removeRoutingRule(routingKey);
        if (trustKey) void api.removeTrustRule(trustKey);
      });
    },
    [api, flashNotice, removeLocally, signals],
  );

  // --- Graduation proposals (X18): the owner's explicit yes/no ------------------------------------
  const decideGraduation = useCallback(
    async (signal: Signal, accept: boolean) => {
      const ruleKey = signal.entityRef;
      // Conservative default: an accept with no explicit pick grants `once`, not `always` (P4-5).
      const scope = ruleKey ? (graduationScope[ruleKey] ?? 'once') : 'once';
      removeLocally(signal.signalId);
      if (!api || !ruleKey) return;
      if (accept) {
        await api.acceptGraduation(ruleKey, scope).catch(() => {});
        void api.actSignal(signal.signalId).catch(() => {});
        flashNotice('Done — Lapo will handle these on its own. Undo any time in the Autonomy panel.');
      } else {
        await api.declineGraduation(ruleKey).catch(() => {});
        void api.dismissSignal(signal.signalId).catch(() => {});
        flashNotice('Kept asking — Lapo won’t bring it up again for a while.');
      }
    },
    [api, removeLocally, flashNotice, graduationScope],
  );

  // RECON R7 — a just-resolved signal only rides the feed to fire its "closed" toast (in `load`); never
  // render it as a card or audit row (it's terminal).
  const visible = signals.filter((s) => s.status !== 'resolved');
  // SIGNAL-NOISE — `task-added` rides the quiet audit line too: the task itself already lives in
  // Todoist/Today, so a second dismissable card was pure chore (owner archive: 20 of 20 dismissed,
  // zero acted). The audit row keeps it visible + set-asideable without demanding a tap.
  const AUDIT_KINDS = new Set(['triage-audit', 'task-added']);
  const auditSignals = visible.filter((s) => AUDIT_KINDS.has(s.kind));
  const cardSignals = visible.filter((s) => !AUDIT_KINDS.has(s.kind));

  // HELM P1-8 — a sent brief keeps the panel alive even on an otherwise-empty day (it's the morning read).
  const hasBrief = Boolean(brief && brief.deliveryId && brief.items.length > 0);
  if (signals.length === 0 && !pending && !notice && !hasBrief) return null;

  return (
    <aside className="lapo-signals" aria-label="Lapo signals" data-testid="day-signals">
      <div className="lapo-signals-head">
        <MaterialIcon name="auto_awesome" size={14} />
        <span className="lapo-signals-title">Lapo</span>
        {cardSignals.length > 0 && <span className="lapo-signals-count">{cardSignals.length}</span>}
        {(cardSignals.length > 0 || auditSignals.length > 0) && (
          <button
            type="button"
            className="lapo-signals-clear"
            title="Dismiss every triage card"
            data-testid="signals-clear-all"
            onClick={() => void clearAll()}
          >
            Clear all
          </button>
        )}
      </div>

      {notice && (
        <div className="lapo-signals-undo" role="status">
          <span>{notice.text}</span>
          {notice.qualityFor && (
            <span className="lapo-signal-feedback" data-testid="notice-quality">
              <button
                type="button"
                className="lapo-signals-undo-btn"
                aria-label="Action was good"
                onClick={() => {
                  void api?.recordSignalFeedback(notice.qualityFor!, { actionQuality: 'good' });
                  setNotice(null);
                }}
              >
                Good
              </button>
              <button
                type="button"
                className="lapo-signals-undo-btn"
                aria-label="Action was not good"
                onClick={() => {
                  const id = notice.qualityFor!;
                  setNotice(null);
                  setTeach({ signalId: id, text: '' });
                  void api?.recordSignalFeedback(id, { actionQuality: 'not-good' });
                }}
              >
                Not good
              </button>
            </span>
          )}
          {notice.undo && (
            <button
              type="button"
              className="lapo-signals-undo-btn"
              onClick={() => {
                notice.undo?.();
                setNotice(null);
              }}
            >
              Undo
            </button>
          )}
        </div>
      )}
      {teach && (
        <div className="lapo-signals-undo lapo-signal-teach" role="dialog" aria-label="Teach Lapo">
          <textarea
            className="lapo-signal-textarea"
            aria-label="What should Lapo do differently?"
            data-testid="teach-box"
            placeholder="Tell Lapo what to do differently next time…"
            value={teach.text}
            onChange={(e) => setTeach({ ...teach, text: e.target.value })}
          />
          <button
            type="button"
            className="lapo-signals-undo-btn"
            data-testid="teach-send"
            disabled={!teach.text.trim()}
            onClick={() => {
              void api?.recordSignalFeedback(teach.signalId, { note: teach.text.trim() });
              setTeach(null);
              flashNotice('Noted — Lapo will factor that in.');
            }}
          >
            Send
          </button>
          <button type="button" className="lapo-signals-undo-btn" onClick={() => setTeach(null)}>
            Cancel
          </button>
        </div>
      )}
      {callFor && (
        <div className="lapo-dialog-backdrop" onMouseDown={() => setCallFor(null)}>
          <div className="lapo-dialog" role="dialog" aria-modal="true" aria-label="Confirm call" data-testid="call-confirm" onMouseDown={(e) => e.stopPropagation()}>
            <h2 className="lapo-dialog-title">Call {callFor.targetName}?</h2>
            <p className="lapo-dialog-body">
              {callFor.resolving
                ? 'Finding a number…'
                : callFor.source
                  ? `Found via ${callFor.source === 'maps' ? 'Google Maps' : callFor.source}. Confirm or edit the number, then Lapo will place the call.`
                  : 'No number found — enter one to call.'}
            </p>
            <input
              className="lapo-teach-textarea"
              style={{ minHeight: 'auto' }}
              type="tel"
              aria-label="Phone number to call"
              data-testid="call-number"
              placeholder="+1 555 123 4567"
              value={callFor.number}
              onChange={(e) => setCallFor((prev) => (prev ? { ...prev, number: e.target.value } : prev))}
            />
            <div className="lapo-dialog-actions">
              <button type="button" className="lapo-btn" onClick={() => setCallFor(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="lapo-btn is-primary"
                data-testid="call-confirm-btn"
                disabled={callFor.resolving || !callFor.number.trim()}
                onClick={() => void confirmCall()}
              >
                Call
              </button>
            </div>
          </div>
        </div>
      )}
      {review && (
        <div className="lapo-dialog-backdrop lapo-review-backdrop" onMouseDown={() => closeReview()}>
          <div
            className="lapo-review"
            role="dialog"
            aria-modal="true"
            aria-label="Review draft"
            data-testid="review-dialog"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="lapo-review-head">
              <h2 className="lapo-review-title">Review draft</h2>
              <button type="button" className="lapo-review-x" aria-label="Close" data-testid="review-close" onClick={() => closeReview()}>
                <MaterialIcon name="close" size={18} />
              </button>
            </div>
            <div className="lapo-review-body">
              <div className="lapo-review-section">
                <div className="lapo-review-label">Original email</div>
                <div className="lapo-review-source" data-testid="review-source">
                  {review.loadingSource ? (
                    <div className="lapo-review-source-empty">Loading email…</div>
                  ) : review.source ? (
                    <>
                      <div className="lapo-review-source-meta">
                        <strong>{review.source.from}</strong>
                        {review.source.subject && <span>{review.source.subject}</span>}
                      </div>
                      {/* Sanitized via DOMPurify (sanitizeHtml) before it reaches the DOM. Plain-text
                          bodies pass through harmlessly as a single text node. */}
                      <div
                        className="lapo-review-source-html"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(review.source.text || review.source.snippet) }}
                      />
                    </>
                  ) : (
                    <div className="lapo-review-source-empty">Original email unavailable.</div>
                  )}
                </div>
              </div>
              <div className="lapo-review-section">
                <div className="lapo-review-label">Your reply</div>
                <textarea
                  className="lapo-review-draft"
                  aria-label="Draft reply"
                  data-testid="review-draft"
                  value={review.draft}
                  disabled={review.regenerating}
                  onChange={(e) => setReview((r) => (r ? { ...r, draft: e.target.value } : r))}
                />
              </div>
              {review.teachOpen && (
                <div className="lapo-review-teach" data-testid="review-teach">
                  <textarea
                    className="lapo-review-teach-input"
                    aria-label="Tell Lapo how to change the draft"
                    placeholder="e.g. Make it warmer and a bit shorter…"
                    value={review.teachText}
                    disabled={review.regenerating}
                    onChange={(e) => setReview((r) => (r ? { ...r, teachText: e.target.value } : r))}
                  />
                  <div className="lapo-review-teach-actions">
                    <button
                      type="button"
                      className="lapo-btn is-primary"
                      data-testid="review-teach-send"
                      disabled={review.regenerating || !review.teachText.trim()}
                      onClick={() => void teachInReview()}
                    >
                      {review.regenerating ? 'Rewriting…' : 'Rewrite draft'}
                    </button>
                    <button
                      type="button"
                      className="lapo-btn"
                      disabled={review.regenerating}
                      onClick={() => setReview((r) => (r ? { ...r, teachOpen: false, teachText: '' } : r))}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="lapo-review-actions">
              <button
                type="button"
                className="lapo-btn lapo-review-teach-btn"
                data-testid="review-teach-open"
                onClick={() => setReview((r) => (r ? { ...r, teachOpen: !r.teachOpen } : r))}
              >
                <MaterialIcon name="school" size={16} /> Teach
              </button>
              <span className="lapo-review-spacer" />
              <button type="button" className="lapo-btn" data-testid="review-cancel" onClick={() => closeReview()}>
                Cancel
              </button>
              <button type="button" className="lapo-btn" data-testid="review-add-draft" disabled={review.regenerating} onClick={addDraftFromReview}>
                <MaterialIcon name="drafts" size={16} /> Add draft
              </button>
              <button
                type="button"
                className="lapo-btn"
                data-testid="review-send"
                disabled={review.regenerating || !review.draft.trim()}
                onClick={() => sendFromReview(false)}
              >
                <MaterialIcon name="send" size={16} /> Send
              </button>
              <button
                type="button"
                className="lapo-btn is-primary"
                data-testid="review-send-archive"
                disabled={review.regenerating || !review.draft.trim()}
                onClick={() => sendFromReview(true)}
              >
                <MaterialIcon name="archive" size={16} /> Send &amp; archive
              </button>
            </div>
          </div>
        </div>
      )}
      {pending && (
        <div className="lapo-signals-undo" role="status">
          <span>
            {pending.mode === 'send' ? 'Sending reply' : pending.mode === 'set-aside' ? 'Set aside' : 'Dismissed'} “{pending.signal.title}”
            {pending.mode === 'send' ? '…' : '.'}
          </span>
          <button type="button" className="lapo-signals-undo-btn" onClick={undo}>
            Undo
          </button>
        </div>
      )}

      {/* HELM P1-8 (C9) — the morning brief: decisions-needed / handled / left-alone, each with the
          same useful/noise/wrong relevance thumbs the signal cards use. The brief IS the delivery (it
          rides the agenda note); this is just its interactive mirror in the feed. Only renders once the
          brief has a deliveryId (it's been sent) so a reaction has a ledger row to patch. */}
      {brief && brief.deliveryId && brief.items.length > 0 && (
        <div className="lapo-signals-brief" data-testid="signals-brief">
          <div className="lapo-signals-head">
            <MaterialIcon name="wb_sunny" size={14} />
            <span className="lapo-signals-title">Morning brief</span>
          </div>
          <ul className="lapo-signals-brief-list">
            {brief.items.map((item, i) => {
              const voteKey = `${brief.deliveryId}:${item.subjectKey}:${i}`;
              const vote = briefVoted.get(voteKey);
              const wrongOpen = briefWrongFor === voteKey;
              const react = (
                reaction: 'useful' | 'noise' | 'wrong',
                wrongCause?: 'wrong-fact' | 'already-handled' | 'duplicate' | 'bad-timing',
              ): void => {
                setBriefWrongFor(null);
                setBriefVoted((prev) => new Map([...prev, [voteKey, reaction]]));
                void api?.recordBriefReaction?.(brief.deliveryId!, reaction, wrongCause);
              };
              // HELM P2-1 (DL-21) — the four-way follow-up a `wrong` tap opens; each cause is captured.
              const WRONG_CAUSES: ReadonlyArray<{ cause: 'wrong-fact' | 'already-handled' | 'duplicate' | 'bad-timing'; label: string }> = [
                { cause: 'wrong-fact', label: 'Wrong fact' },
                { cause: 'already-handled', label: 'Already handled' },
                { cause: 'duplicate', label: 'Duplicate' },
                { cause: 'bad-timing', label: 'Bad timing' },
              ];
              return (
                <li key={voteKey} className="lapo-signals-brief-item" data-section={item.section} data-testid={`brief-item-${i}`}>
                  <div className="lapo-signals-brief-text">
                    <span className="lapo-signals-brief-item-title">{item.title}</span>
                    <span className="lapo-signals-brief-item-detail">{item.detail}</span>
                  </div>
                  <div className="lapo-signal-feedback">
                    {vote ? (
                      <span className="lapo-signal-feedback-done" data-testid={`brief-voted-${i}`}>Noted</span>
                    ) : wrongOpen ? (
                      <div className="lapo-signal-feedback-wrongcause" data-testid={`brief-wrongcause-${i}`}>
                        {WRONG_CAUSES.map(({ cause, label }) => (
                          <button
                            key={cause}
                            type="button"
                            className="lapo-signal-feedback-wrongcause-btn"
                            data-testid={`brief-wrongcause-${cause}-${i}`}
                            onClick={() => react('wrong', cause)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="lapo-signal-feedback-btn"
                          aria-label="Useful"
                          title="Useful — more like this"
                          data-testid={`brief-useful-${i}`}
                          onClick={() => react('useful')}
                        >
                          <MaterialIcon name="thumb_up" size={13} />
                        </button>
                        <button
                          type="button"
                          className="lapo-signal-feedback-btn"
                          aria-label="Noise"
                          title="Noise — less like this"
                          data-testid={`brief-noise-${i}`}
                          onClick={() => react('noise')}
                        >
                          <MaterialIcon name="thumb_down" size={13} />
                        </button>
                        <button
                          type="button"
                          className="lapo-signal-feedback-btn"
                          aria-label="Wrong"
                          title="Wrong — tell Lapo what was wrong"
                          data-testid={`brief-wrong-${i}`}
                          onClick={() => setBriefWrongFor(voteKey)}
                        >
                          <MaterialIcon name="error_outline" size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* The collapsed audit line — everything Lapo handled on its own (spec A4). */}
      {auditSignals.length > 0 && (
        <div className="lapo-signals-audit" data-testid="signals-audit">
          <button type="button" className="lapo-signals-audit-head" onClick={() => setAuditOpen((o) => !o)} aria-expanded={auditOpen}>
            <MaterialIcon name={auditOpen ? 'expand_more' : 'chevron_right'} size={16} />
            <MaterialIcon name="inbox" size={15} />
            <span>
              Lapo handled {auditSignals.length} {auditSignals.length === 1 ? 'item' : 'items'}
            </span>
          </button>
          {auditOpen && (
            <ul className="lapo-signals-audit-list">
              {auditSignals.map((s) => {
                // CULL — an auto-archived spam row carries a "Not spam" rescue action: unarchive the
                // message, cancel the pending rule, record the negative example. Executes through the
                // same act path as card actions.
                const rescue = s.actions.find((a) => a.kind === 'rescue-spam');
                return (
                  <li key={s.signalId} data-testid={`audit-${s.signalId}`}>
                    <span className="lapo-audit-title">{s.title}</span>
                    <span className="lapo-audit-what">{s.detail}</span>
                    {rescue && (
                      <button
                        type="button"
                        className="lapo-audit-rescue"
                        onClick={() => void act(s, { actionIndex: s.actions.indexOf(rescue) })}
                      >
                        {rescue.label}
                      </button>
                    )}
                    <button type="button" className="lapo-signal-dismiss" aria-label="Set aside" onClick={() => setAside(s)}>
                      <MaterialIcon name="close" size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* The cards that actually need the owner: proposals + escalations + legacy signals. */}
      <ul className="lapo-signals-list">
        {cardSignals.map((s) => {
          const rolledForward = s.dateKey !== dateKey;
          const sender = senderOf(s);
          const draft = editableDraft(s);
          const primary = primaryAction(s);
          const primaryIndex = primary ? s.actions.indexOf(primary) : -1;
          const isEditing = editing?.signalId === s.signalId;
          const isInfo = s.priority === 'info';
          const isProposalCard = s.kind === 'graduation-proposal';
          const isResurfaceCard = s.kind === 'resurface-digest';
          const isFilteredCard = s.kind === 'ingest-filtered';
          const isRoutingProposal = s.kind === 'routing-proposal';
          const isTriageCard = s.kind === 'action-proposed' || s.kind === 'action-escalated';
          const hasMessage = Boolean(primary?.payload?.messageId);
          const trustKey = trustKeyOf(s);
          return (
            <li
              key={s.signalId}
              ref={(el) => observe(el, s)}
              className={`lapo-signal is-${s.priority}${s.seenAt ? '' : ' is-unseen'}`}
              data-testid={`signal-${s.signalId}`}
              data-kind={s.kind}
            >
              <span
                className="lapo-signal-icon"
                title={triageSource(s) === 'calendar' ? 'Calendar event' : triageSource(s) === 'email' ? 'Email' : undefined}
              >
                <MaterialIcon name={iconFor(s)} size={18} />
              </span>
              <div className="lapo-signal-body">
                <div className="lapo-signal-title lapo-clamp-1">
                  {!s.seenAt && <span className="lapo-signal-dot" aria-label="new" />}
                  {s.title}
                  {rolledForward && (
                    <span className="lapo-signal-origin" title={`From ${s.dateKey}`}>
                      {s.dateKey}
                    </span>
                  )}
                </div>
                {formatWhen(s) && (
                  <div className="lapo-signal-when" data-testid={`when-${s.signalId}`}>
                    <MaterialIcon name="schedule" size={13} />
                    <span>{formatWhen(s)}</span>
                  </div>
                )}
                <div className="lapo-signal-reason">{s.reason}</div>
                {s.detail && !isEditing && !isResurfaceCard && !isFilteredCard && <div className="lapo-signal-detail lapo-clamp-2">{s.detail}</div>}
                {isEditing && (
                  <div className="lapo-signal-editor">
                    <textarea
                      className="lapo-signal-textarea"
                      aria-label="Edit draft reply"
                      data-testid={`edit-${s.signalId}`}
                      value={editing!.body}
                      onChange={(e) => setEditing({ signalId: s.signalId, body: e.target.value })}
                    />
                    <div className="lapo-signal-actions">
                      <button type="button" className="lapo-signal-primary" onClick={() => void act(s, { editedBody: editing!.body })}>
                        Create draft
                      </button>
                      <button type="button" className="lapo-signal-snooze" onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {/* Graduation proposal (X18 / HELM P4-5): evidence + a scope picker (once|subject|always,
                    default the conservative `once`) + the explicit yes/no pair. The chosen scope is
                    written as a P4-2 grant; broaden or revoke later from the Autonomy panel. */}
                {!isEditing && isProposalCard && (
                  <div className="lapo-signal-actions">
                    <select
                      className="lapo-signal-snooze"
                      aria-label="How broadly to let Lapo auto-handle this"
                      data-testid={`graduate-scope-${s.signalId}`}
                      value={s.entityRef ? (graduationScope[s.entityRef] ?? 'once') : 'once'}
                      onChange={(e) => {
                        const ruleKey = s.entityRef;
                        if (!ruleKey) return;
                        const next = e.target.value as GraduationScope;
                        setGraduationScope((prev) => ({ ...prev, [ruleKey]: next }));
                      }}
                    >
                      {GRADUATION_SCOPES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="lapo-signal-primary"
                      data-testid={`graduate-yes-${s.signalId}`}
                      onClick={() => void decideGraduation(s, true)}
                    >
                      Yes, auto this
                    </button>
                    <button
                      type="button"
                      className="lapo-signal-secondary"
                      data-testid={`graduate-no-${s.signalId}`}
                      onClick={() => void decideGraduation(s, false)}
                    >
                      Keep asking
                    </button>
                  </div>
                )}
                {/* ECHO — the weekly resurface digest (D4/D7): item list rendered from actions[]
                    (title → open, whyNow line, per-item "Not relevant"), never the unified face. */}
                {!isEditing && isResurfaceCard && (
                  <div className="lapo-signal-resurface" data-testid={`resurface-${s.signalId}`}>
                    {s.actions
                      .filter((a) => a.payload?.targetId && !droppedResurface.has(`${s.signalId}:${a.payload.targetId}`))
                      .map((a, i) => (
                        <div key={a.payload!.targetId} className="lapo-signal-resurface-item" data-testid={`resurface-item-${i}`}>
                          <button
                            type="button"
                            className="lapo-signal-secondary"
                            onClick={() => {
                              if (a.kind === 'open-note' && a.payload?.noteId) onOpenNote?.(a.payload.noteId);
                              else if (a.kind === 'link' && a.payload?.url?.startsWith('#')) window.location.hash = a.payload.url;
                              else if (a.kind === 'link' && a.payload?.url) window.open(a.payload.url, '_blank', 'noopener');
                            }}
                          >
                            {a.label}
                          </button>
                          {a.payload?.whyNow && <span className="lapo-signal-reason">{a.payload.whyNow}</span>}
                          <button
                            type="button"
                            className="lapo-signal-snooze"
                            aria-label={`Not relevant: ${a.label}`}
                            data-testid={`resurface-drop-${i}`}
                            onClick={() => {
                              const targetId = a.payload!.targetId!;
                              setDroppedResurface((prev) => new Set([...prev, `${s.signalId}:${targetId}`]));
                              void api?.resurfaceNotRelevant(targetId).catch(() => {});
                              flashNotice('Won’t resurface that again soon');
                            }}
                          >
                            Not relevant
                          </button>
                        </div>
                      ))}
                    <div className="lapo-signal-actions">
                      <button type="button" className="lapo-signal-snooze" onClick={() => dismiss(s)}>
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}
                {/* SIEVE — the collapsed "filtered N" card (S8): parked items with one-tap restore. */}
                {!isEditing && isFilteredCard && (
                  <div className="lapo-signal-resurface" data-testid={`filtered-${s.signalId}`}>
                    {s.actions
                      .filter((a) => a.payload?.parkedId && !restoredParked.has(a.payload.parkedId))
                      .map((a, i) => (
                        <div key={a.payload!.parkedId} className="lapo-signal-resurface-item" data-testid={`filtered-item-${i}`}>
                          <span className="lapo-signal-reason">{a.label}</span>
                          <button
                            type="button"
                            className="lapo-signal-secondary"
                            aria-label={`Restore ${a.label}`}
                            data-testid={`filtered-restore-${i}`}
                            onClick={() => {
                              const parkedId = a.payload!.parkedId!;
                              void api?.restoreParkedIngest(parkedId).then((ok) => {
                                if (ok) {
                                  setRestoredParked((prev) => new Set([...prev, parkedId]));
                                  flashNotice('Restoring — it will reappear shortly');
                                }
                              });
                            }}
                          >
                            Restore
                          </button>
                        </div>
                      ))}
                    <div className="lapo-signal-actions">
                      <button type="button" className="lapo-signal-snooze" onClick={() => dismiss(s)}>
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}
                {/* TUNE U7 — routing proposal: the explicit yes/no pair; accepting writes the
                    RoutingRule through the EXISTING routing machinery (never silent). */}
                {!isEditing && isRoutingProposal && (
                  <div className="lapo-signal-actions">
                    <button
                      type="button"
                      className="lapo-signal-primary"
                      data-testid={`routing-yes-${s.signalId}`}
                      onClick={() => {
                        const ruleKey = s.actions[0]?.payload?.ruleKey;
                        if (ruleKey) void api?.setRoutingRule({ ruleKey, loudness: 'suppress' });
                        removeLocally(s.signalId);
                        void api?.dismissSignal(s.signalId);
                        flashNotice('Done — those won’t show anymore (undo in Autonomy → Rules).');
                      }}
                    >
                      Stop showing these
                    </button>
                    <button
                      type="button"
                      className="lapo-signal-secondary"
                      data-testid={`routing-no-${s.signalId}`}
                      onClick={() => {
                        removeLocally(s.signalId);
                        void api?.dismissSignal(s.signalId);
                      }}
                    >
                      Keep them
                    </button>
                  </div>
                )}
                {/* The unified face (X13): ONE primary verb + Edit + the single ⋯ overflow. */}
                {!isEditing && !isProposalCard && !isResurfaceCard && !isFilteredCard && !isRoutingProposal && (
                  <div className="lapo-signal-actions">
                    {primary && (
                      <button
                        type="button"
                        className="lapo-signal-primary"
                        onClick={() =>
                          isReviewableDraft(s)
                            ? openReview(s)
                            : void act(s, { actionIndex: primaryIndex >= 0 ? primaryIndex : undefined })
                        }
                      >
                        {verbFor(primary)}
                      </button>
                    )}
                    {draft && (
                      <button
                        type="button"
                        className="lapo-signal-secondary"
                        onClick={() => setEditing({ signalId: s.signalId, body: draft.payload?.draftBody ?? '' })}
                      >
                        Edit
                      </button>
                    )}
                    <div className="lapo-signal-tune">
                      <button
                        type="button"
                        className="lapo-signal-tune-btn"
                        aria-label="More"
                        data-testid={`tune-${s.signalId}`}
                        onClick={() => setMenuFor((m) => (m === s.signalId ? null : s.signalId))}
                      >
                        <MaterialIcon name="more_horiz" size={16} />
                      </button>
                      {menuFor === s.signalId && (
                        <div className="lapo-signal-tune-menu" role="menu" data-testid={`menu-${s.signalId}`}>
                          {/* ── HONE: ⋯ is a pure Create/Remove action palette. Rules moved to 👎. ── */}
                          <div className="lapo-signal-menu-group" role="presentation">
                            Create
                          </div>
                          {/* The card's own alternative actions (e.g. RSVP Decline) stay tappable here. */}
                          {s.actions.map((a, i) =>
                            i === primaryIndex || a.kind === 'snooze' || a.kind === 'dismiss' ? null : (
                              <button key={`act-${i}`} type="button" role="menuitem" onClick={() => void act(s, { actionIndex: i })}>
                                {verbFor(a)}
                              </button>
                            ),
                          )}
                          {createActionsFor(s).map((kind) => (
                            <button
                              key={kind}
                              type="button"
                              role="menuitem"
                              data-testid={`create-${kind}-${s.signalId}`}
                              // Draft routes through the grounded async draft generator; Call resolves a
                              // number then CONFIRMS before dialing; the rest hit the create-artifact endpoint.
                              onClick={() =>
                                kind === 'draft' ? requestDraft(s) : kind === 'call' ? void startCall(s) : void createArtifact(s, kind)
                              }
                            >
                              <MaterialIcon name={CREATE_META[kind].icon} size={14} /> {CREATE_META[kind].label}
                            </button>
                          ))}
                          {(s.linkedArtifacts?.length ?? 0) > 0 && (
                            <>
                              <div className="lapo-signal-menu-group" role="presentation">
                                Remove
                              </div>
                              {s.linkedArtifacts!.map((a) => (
                                <button
                                  key={a.artifactId}
                                  type="button"
                                  role="menuitem"
                                  className="danger"
                                  data-testid={`remove-${a.artifactId}`}
                                  onClick={() => void removeArtifact(s, a)}
                                >
                                  <MaterialIcon name="undo" size={14} /> {a.label}
                                </button>
                              ))}
                            </>
                          )}
                          {!isInfo && (
                            <>
                              <div className="lapo-signal-menu-group" role="presentation">
                                Later
                              </div>
                              <button type="button" role="menuitem" title="Hide until tomorrow" onClick={() => snooze(s, 1)}>
                                Tomorrow
                              </button>
                              <button type="button" role="menuitem" title="Hide until next week" onClick={() => snooze(s, 7)}>
                                Next week
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            role="menuitem"
                            aria-label="Dismiss"
                            title="Dismiss — seen it, no action needed"
                            onClick={() => dismiss(s)}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* TUNE U1 — the UNIFORM capture row on EVERY kind: relevance pair + Teach. Feedback
                    never touches the card lifecycle (a 👎 keeps the card; Dismiss is separate). */}
                {!isEditing && (
                  <div className="lapo-signal-feedback" data-testid={`feedback-${s.signalId}`}>
                    {voted.has(s.signalId) ? (
                      <span className="lapo-signal-feedback-done">Noted{voted.get(s.signalId) === 'not-useful' ? ' — less of this' : ''}</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="lapo-signal-feedback-btn"
                          aria-label="Useful"
                          title="Useful — more like this"
                          data-testid={`useful-${s.signalId}`}
                          onClick={() => {
                            setVoted((prev) => new Map([...prev, [s.signalId, 'useful' as const]]));
                            void api?.recordSignalFeedback(s.signalId, { relevance: 'useful' });
                          }}
                        >
                          <MaterialIcon name="thumb_up" size={13} />
                        </button>
                        <button
                          type="button"
                          className="lapo-signal-feedback-btn"
                          aria-label="Not useful"
                          title="Not useful — less like this"
                          data-testid={`not-useful-${s.signalId}`}
                          onClick={() => {
                            setVoted((prev) => new Map([...prev, [s.signalId, 'not-useful' as const]]));
                            void api?.recordSignalFeedback(s.signalId, { relevance: 'not-useful' });
                            // HONE — a 👎 offers the rule-tuning follow-up (relocated out of ⋯).
                            setFollowUpFor(s.signalId);
                          }}
                        >
                          <MaterialIcon name="thumb_down" size={13} />
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="lapo-signal-feedback-btn"
                      aria-label={`Teach Lapo about ${s.title}`}
                      data-testid={`teach-${s.signalId}`}
                      onClick={() => setTeach({ signalId: s.signalId, text: '' })}
                    >
                      Teach
                    </button>
                  </div>
                )}
                {/* HONE — the 👎 relevance follow-up: the trust/routing rules relocated out of ⋯. */}
                {!isEditing && followUpFor === s.signalId && (
                  <div className="lapo-signal-followup" role="group" data-testid={`followup-${s.signalId}`}>
                    {sender && (
                      <button
                        type="button"
                        className="lapo-signal-snooze"
                        data-testid={`suppress-${s.signalId}`}
                        title="Stop showing signals like this. The action will NOT run on its own."
                        onClick={() => tuneRouting(s, { loudness: 'suppress' }, { clearCard: true })}
                      >
                        Never show these
                      </button>
                    )}
                    <button
                      type="button"
                      className="lapo-signal-snooze"
                      onClick={() => tuneRouting(s, { loudness: 'quiet' })}
                    >
                      Show quietly
                    </button>
                    {sender && trustKey && primary && !actionDecl(primary.kind)?.navigation && (
                      <button
                        type="button"
                        className="lapo-signal-snooze"
                        data-testid={`always-${s.signalId}`}
                        onClick={() => tuneTrust(s, 'auto')}
                      >
                        Always {ruleVerbFor(s)} from {sender}
                      </button>
                    )}
                    {isTriageCard && hasMessage && (
                      <button
                        type="button"
                        className="lapo-signal-snooze"
                        data-testid={`automated-${s.signalId}`}
                        title="This is a no-reply / notification sender — handle its mail quietly"
                        onClick={() => void markAutomated(s)}
                      >
                        Automated
                      </button>
                    )}
                    {isTriageCard && hasMessage && (
                      <button
                        type="button"
                        className="lapo-signal-snooze"
                        data-testid={`spam-${s.signalId}`}
                        onClick={() => void markSpam(s)}
                      >
                        Spam
                      </button>
                    )}
                    <button
                      type="button"
                      className="lapo-signal-snooze"
                      aria-label="Dismiss suggestions"
                      onClick={() => setFollowUpFor(null)}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
              {/* Always-visible dismiss X (upper-right): "seen it, no action needed" with the same
                  undo-backed dismiss as the ⋯ menu. */}
              <button
                type="button"
                className="lapo-signal-close"
                aria-label="Dismiss"
                title="Dismiss — seen it, no action needed"
                data-testid={`dismiss-${s.signalId}`}
                onClick={() => dismiss(s)}
              >
                <MaterialIcon name="close" size={15} />
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

export const DaySignals = memo(DaySignalsInner);
